/**
 * @fileoverview Letterhead override picker
 *
 * Shared dropdown for picking a letterhead at either the per-document
 * level or per-template level. The override shape is the same in both
 * cases — a tagged union { kind: "none" | "letterhead" | "variant" } —
 * so the picker is identical apart from labels.
 *
 * Sections (in order, each conditional on what exists):
 *   1. "Use firm default" row — only when a firm default exists and the
 *      current entity carries an override.
 *   2. ★ Favourites — bundle + variant favourites mixed.
 *   3. Letterheads — every bundle.
 *   4. Variants — every named variant recipe.
 *   5. "No letterhead" — variant-free render.
 */

import type { Id } from "~/convex/_generated/dataModel";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import { Check, ChevronDown, FileText, RotateCcw, Star } from "lucide-react";
import { cn } from "@/utils/misc";

export type LetterheadOverride =
  | { kind: "none" }
  | { kind: "letterhead"; bundleName: string }
  | { kind: "variant"; variantId: Id<"letterheadVariants"> };

export type LetterheadOverridePickerProps = {
  currentOverride?: LetterheadOverride | null;
  firmDefaultBundleName: string | null;
  favouriteBundleNames: string[];
  allBundles: string[];
  allVariants: Array<{
    _id: Id<"letterheadVariants">;
    name: string;
    isFavourite?: boolean;
  }>;
  onChange: (override: LetterheadOverride | null) => void | Promise<void>;
  /**
   * Customise the "Use firm default …" row's wording. For a per-document
   * context where the template has its own override, callers may want
   * "Use template default (…)" instead.
   */
  clearLabel?: string;
  /** Tooltip on the trigger button. */
  triggerTitle?: string;
  /** Override what the trigger button displays. Defaults to the resolved name. */
  triggerLabel?: string;
};

export function LetterheadOverridePicker({
  currentOverride,
  firmDefaultBundleName,
  favouriteBundleNames,
  allBundles,
  allVariants,
  onChange,
  clearLabel,
  triggerTitle,
  triggerLabel,
}: LetterheadOverridePickerProps) {
  // What's actually being rendered right now? Surface in the button.
  const resolvedLabel = (() => {
    if (currentOverride?.kind === "none") return "No letterhead";
    if (currentOverride?.kind === "letterhead") {
      return currentOverride.bundleName;
    }
    if (currentOverride?.kind === "variant") {
      const v = allVariants.find((x) => x._id === currentOverride.variantId);
      return v?.name ?? "Variant";
    }
    if (firmDefaultBundleName) {
      return `${firmDefaultBundleName} (firm default)`;
    }
    if (allBundles.length === 1) return allBundles[0];
    return "No letterhead";
  })();
  const buttonLabel = triggerLabel ?? resolvedLabel;

  // Check helper — should this row show a tick?
  const isActive = (kind: "letterhead" | "variant" | "none", id: string) => {
    if (kind === "none") return currentOverride?.kind === "none";
    if (kind === "letterhead") {
      return (
        currentOverride?.kind === "letterhead" &&
        currentOverride.bundleName === id
      );
    }
    return (
      currentOverride?.kind === "variant" && currentOverride.variantId === id
    );
  };

  const favouriteVariants = allVariants.filter((v) => v.isFavourite);
  const hasAnyFavourites =
    favouriteBundleNames.length > 0 || favouriteVariants.length > 0;
  const overrideActive = !!currentOverride;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-primary/70 transition hover:border-primary/30 hover:bg-secondary/60 focus:outline-none focus:ring-2 focus:ring-primary/20"
          title={triggerTitle ?? "Choose a letterhead"}
        >
          <FileText className="size-3.5 text-primary/40" />
          <span className="max-w-[140px] truncate">{buttonLabel}</span>
          <ChevronDown className="size-3 text-primary/40" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        {/* Clear override → use firm default */}
        {overrideActive && firmDefaultBundleName && (
          <>
            <DropdownMenuItem
              onClick={() => void onChange(null)}
              className="text-xs"
            >
              <RotateCcw className="mr-2 size-3.5 text-primary/40" />
              <span className="truncate">
                {clearLabel ?? `Use firm default (${firmDefaultBundleName})`}
              </span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        {/* Favourites */}
        {hasAnyFavourites && (
          <>
            <DropdownMenuLabel className="text-[10px] font-medium uppercase tracking-wide text-primary/40">
              ★ Favourites
            </DropdownMenuLabel>
            {favouriteBundleNames.map((name) => (
              <DropdownMenuItem
                key={`fav-bundle-${name}`}
                onClick={() =>
                  void onChange({ kind: "letterhead", bundleName: name })
                }
                className="text-xs"
              >
                <Check
                  className={cn(
                    "mr-2 size-3.5 shrink-0",
                    isActive("letterhead", name)
                      ? "text-primary opacity-100"
                      : "opacity-0",
                  )}
                />
                <Star className="mr-1 size-3 shrink-0 text-amber-500" />
                <span className="truncate">{name}</span>
              </DropdownMenuItem>
            ))}
            {favouriteVariants.map((v) => (
              <DropdownMenuItem
                key={`fav-variant-${v._id}`}
                onClick={() =>
                  void onChange({ kind: "variant", variantId: v._id })
                }
                className="text-xs"
              >
                <Check
                  className={cn(
                    "mr-2 size-3.5 shrink-0",
                    isActive("variant", v._id)
                      ? "text-primary opacity-100"
                      : "opacity-0",
                  )}
                />
                <Star className="mr-1 size-3 shrink-0 text-amber-500" />
                <span className="truncate">{v.name}</span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
          </>
        )}

        {/* Letterheads */}
        {allBundles.length > 0 && (
          <>
            <DropdownMenuLabel className="text-[10px] font-medium uppercase tracking-wide text-primary/40">
              Letterheads
            </DropdownMenuLabel>
            {allBundles.map((name) => (
              <DropdownMenuItem
                key={`bundle-${name}`}
                onClick={() =>
                  void onChange({ kind: "letterhead", bundleName: name })
                }
                className="text-xs"
              >
                <Check
                  className={cn(
                    "mr-2 size-3.5 shrink-0",
                    isActive("letterhead", name)
                      ? "text-primary opacity-100"
                      : "opacity-0",
                  )}
                />
                <span className="truncate">{name}</span>
                {firmDefaultBundleName === name && (
                  <span className="ml-auto rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-medium text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300">
                    default
                  </span>
                )}
              </DropdownMenuItem>
            ))}
            {allVariants.length > 0 && <DropdownMenuSeparator />}
          </>
        )}

        {/* Variants */}
        {allVariants.length > 0 && (
          <>
            <DropdownMenuLabel className="text-[10px] font-medium uppercase tracking-wide text-primary/40">
              Variants
            </DropdownMenuLabel>
            {allVariants.map((v) => (
              <DropdownMenuItem
                key={`var-${v._id}`}
                onClick={() =>
                  void onChange({ kind: "variant", variantId: v._id })
                }
                className="text-xs"
              >
                <Check
                  className={cn(
                    "mr-2 size-3.5 shrink-0",
                    isActive("variant", v._id)
                      ? "text-primary opacity-100"
                      : "opacity-0",
                  )}
                />
                <span className="truncate">{v.name}</span>
              </DropdownMenuItem>
            ))}
          </>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => void onChange({ kind: "none" })}
          className="text-xs"
        >
          <Check
            className={cn(
              "mr-2 size-3.5 shrink-0",
              currentOverride?.kind === "none"
                ? "text-primary opacity-100"
                : "opacity-0",
            )}
          />
          <span className="truncate">No letterhead</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
